<template>
  <Modal
    class="comment-modal-container"
    is-visible
    :title="textContent('header')"
    @onClose="handleClickClose"
  >
    <template #body>
      <div
        v-if="isReview && hasReviewed"
        class="font-size-base py-5 text-center"
      >
        Thanks for contributing your review for this deal!
      </div>
      <div v-else>
        <div
          v-if="parentComment"
          class="comment-instructions mb-2"
        >
          <p
            v-if="parentComment.title"
            class="font-weight-bold"
          >
            {{ parentComment.title }}
          </p>
          <div
            ref="parentComment"
            class="parent-comment"
            :class="{ 'parent-comment-full': showMore }"
          >
            <PDPCommentBody
              :comment="parentComment.comment"
            />
          </div>
          <button
            v-if="enableShowMore"
            class="btn btn-link p-0 font-weight-normal pdp-text btn-show-more"
            @click="toggleShowMore"
            @keydown.enter="toggleShowMore"
          >
            {{ showMore ? 'Show less' : 'See more' }}
          </button>
        </div>
        <CommentReview
          v-if="isReview"
          :rating="rating"
          :title="commentTitle"
          :is-valid="isValidTitle"
          @update-rating="updateRating"
          @on-change-title="onChangeTitle"
        />
        <div
          class="comment-textarea"
          :class="{ 'comment-invalid': !isValidComment }"
        >
          <div v-if="isReview" class="d-flex">
            <label class="mr-1" for="comment-input">
              Review:
            </label>
            <!-- eslint-disable vue-a11y/label-has-for -->
            <span class="error-text">Please enter a {{ typeCopy }}</span>
          </div>
          <label v-else-if="isReply" for="comment-input">
            Your reply:
          </label>
          <textarea
            id="comment-input"
            v-model="commentText"
            class="form-control"
            :class="{ 'is-invalid': !isValidComment }"
            rows="7"
            maxlength="3000"
            :placeholder="textContent('placeholder')"
          />
        </div>
        <div class="py-3">
          <LegacyCheckbox
            :label="textContent('notify')"
            @on-click="onClickCheckbox"
          />
        </div>
        <div class="comment-instructions">
          <h5 class="mb-2">
            {{ textContent('instructionTitle') }}
          </h5>
          <ul class="list-unstyled">
            <li
              v-for="point in textContent('instructionPoints')"
              :key="point"
              class="comment-instructions-item"
            >
              <span
                v-for="(partial, index) in point.split('||')"
                :key="partial"
              >
                <span v-if="index === 0">{{ partial }}</span>
                <a
                  v-else-if="index === 1"
                  class="comment-email text-"
                  :href="`mailto:${partial}`"
                >
                  {{ partial }}
                </a>
                <span v-else-if="index === 2">{{ partial }}</span>
              </span>
            </li>
          </ul>
        </div>

        <div class="comment-dos-dont mt-3">
          By submitting, I agree to
          <a
            href="/community-guidelines/"
            target="_blank"
            rel="noopener"
            aria-label="click here for further details about AppSumo's community guidelines"
          >
            AppSumo's community guidelines
          </a>
        </div>
      </div>
    </template>

    <template
      v-if="!isReview || (isReview && !hasReviewed)"
      #footer
    >
      <button
        class="btn btn-outline-secondary px-4"
        type="button"
        :disabled="isProcessing"
        @click="handleClickClose"
        @keydown.enter="handleClickClose"
      >
        <span>Cancel</span>
      </button>
      <button
        class="btn btn-primary px-4 submit"
        type="button"
        data-testid="submit-button"
        :disabled="!commentText || isProcessing"
        @click="onClickSubmit"
        @keydown.enter="onClickSubmit"
      >
        <span>{{ textContent('submit') }}</span>
      </button>
    </template>
  </Modal>
</template>

<script>
// TODO:BRO-1276: import DModal from '@appsumo/dorado-nuxt';
import PDPCommentBody from '../comment/PDPCommentBody.vue';
import CommentReview from './PDPCommentReview.vue';
import LegacyCheckbox from '../../ui/LegacyCheckbox.vue';
import Modal from '../../common/Modal.vue';
import { DiscussionType } from '../../../constants/discussions';

const TYPE_COPY = {
  dealcomment: 'question',
  dealreview: 'review',
  reply: 'reply',
};

export default {
  name: 'PDPCommentModal',
  components: {
    PDPCommentBody,
    CommentReview,
    LegacyCheckbox,
    Modal,
  },
  props: {
    type: {
      type: String,
      default: 'dealcomment',
    },
    hasReviewed: {
      type: Boolean,
    },
    parentComment: {
      type: Object,
      default: () => null,
    },
  },
  emits: ['click-close', 'on-submit'],
  data() {
    return {
      commentText: '',
      commentTitle: '',
      rating: 5,
      notifyEmail: false,
      isValidComment: true,
      isValidTitle: true,
      isProcessing: false,
      enableShowMore: false,
      showMore: false,
      parentCommentObserver: null,
      parentCommentPreviousWidth: 0,
    };
  },
  computed: {
    isReview() {
      return this.type === DiscussionType.review;
    },
    isReply() {
      return this.type === TYPE_COPY.reply;
    },
    typeCopy() {
      return TYPE_COPY[this.type];
    },
  },
  watch: {
    commentText() {
      if (!this.isValidComment && this.commentText && this.commentText.length) {
        this.isValidComment = true;
      }
    },
    commentTitle() {
      if (!this.isValidTitle && this.commentTitle && this.commentTitle.length) {
        this.isValidTitle = true;
      }
    },
  },
  created() {
    this.TEXT_TREE = {
      dealcomment: {
        header: 'Submit a Question',
        placeholder: 'Enter your question',
        notify: 'Notify me via email when someone replies to this question',
        instructionTitle: 'Please check these guidelines before submitting a question:',
        instructionPoints: [
          'Check the FAQ and pinned comments before you post, your question may have already been answered!',
          'Please don’t use this forum as a platform for requesting deal features or new terms.',
          'If you have an AppSumo support related issue, shoot us an email at ||support@appsumo.com||.',
          'Be respectful of you fellow Sumo-lings and partners – rudeness or harassment will not be tolerated.',
        ],
        submit: 'Submit Question',
      },
      reply: {
        header: `Reply to ${this.parentComment?.username}`,
        placeholder: 'Enter your response',
        notify: 'Notify me via email for follow-ups',
        instructionTitle: 'Please check these guidelines before submitting a review:',
        instructionPoints: [
          'Please don’t use this forum as a platform for requesting deal features or new terms.',
          'If you have an AppSumo support related issue, shoot us an email at ||support@appsumo.com||.',
          'Be respectful of you fellow Sumo-lings and partners – rudeness or harassment will not be tolerated.',
        ],
        submit: 'Submit',
      },
      dealreview: {
        header: 'Submit a review',
        placeholder: 'Enter your review',
        notify: 'Notify me via email when someones replies to this review',
        instructionTitle: 'Please check these guidelines before submitting your review:',
        instructionPoints: [
          'Please review the product you\'re purchasing, not the terms of the deal.',
          'Add value when leaving a review.',
          'Try the product before reviewing it!',
        ],
        submit: 'Submit review',
      },
    };
  },
  mounted() {
    if (this.$refs.parentComment) {
      this.setParentCommentObserver();
    }
  },
  beforeDestroy() {
    if (this.parentCommentObserver) {
      this.parentCommentObserver.unobserve(this.$refs.parentComment);
    }
  },
  methods: {
    setParentCommentObserver() {
      this.parentCommentObserver = new ResizeObserver((entries) => {
        const entry = entries.find((item) => item.target === this.$refs.parentComment);

        if (!entry) {
          return;
        }

        const { width } = entry.contentRect;

        // only if element width has change
        if (width !== this.parentCommentPreviousWidth) {
          // reset state to make sure we validate element height correctly
          this.showMore = false;

          this.toogleEnableShowMore();
          this.parentCommentPreviousWidth = width;
        }
      });

      this.parentCommentObserver.observe(this.$refs.parentComment);
    },
    onClickCheckbox(notify) {
      this.notifyEmail = notify;
    },
    handleClickClose() {
      if (!this.isProcessing) {
        this.$emit('click-close');
      }
    },
    onClickSubmit() {
      // jw: if we are already processing there is no reason to do anything else.
      if (this.isProcessing) {
        return;
      }

      // jl: cannot use ?. syntax here (breaks test case)
      const isValidComment = !!(this.commentText && this.commentText.length);
      const isValidTitle = !!(this.commentTitle && this.commentTitle.length);
      const isReview = this.type === DiscussionType.review;

      // jw: we only need a valid title for reviews.
      if (isValidComment && (!isReview || isValidTitle)) {
        // jw: once the on-submit handler finishes this modal will be closed and this flag will no
        //     longer exist. See PDPDiscussion.onSubmitComment where it calls toggleCreateItemModal
        this.isProcessing = true;

        this.$emit('on-submit', {
          comment: this.commentText,
          notifyEmail: this.notifyEmail,
          rating: isReview ? this.rating : null,
          title: this.commentTitle,
          type: this.type,
        });

        return;
      }

      if (isReview) {
        this.isValidTitle = isValidTitle;
      }

      this.isValidComment = isValidComment;
    },
    textContent(key) {
      return (this.TEXT_TREE[this.type] && this.TEXT_TREE[this.type][key]) || '';
    },

    updateRating(rating) {
      this.rating = rating;
    },

    onChangeTitle(title) {
      this.commentTitle = title;
    },

    toogleEnableShowMore() {
      const { scrollHeight, clientHeight } = this.$refs.parentComment;
      this.enableShowMore = scrollHeight > clientHeight;
    },

    toggleShowMore() {
      this.showMore = !this.showMore;
    },
  },
};
</script>

<style lang="scss" scoped>
// TODO: Remove this when dorado is implemented
@import '~/styles/dorado/main';
@import '~/styles/utils/breakpoints';

.comment-modal-container ::v-deep .modal-body {
  box-shadow: none !important;
  padding-bottom: 16px;
}

.comment-modal-container ::v-deep .checkbox-label {
  font-family: Roboto, Verdana, sans-serif;
  font-size: 14px !important;
  font-weight: normal;
}

.parent-comment {
  /* stylelint-disable-next-line */
  display: -webkit-box;
  overflow: hidden;
  /* stylelint-disable-next-line */
  -webkit-box-orient: vertical;
  /* stylelint-disable-next-line */
  -webkit-line-clamp: 4;

  &.parent-comment-full {
    display: block;
  }
}

.btn-show-more:focus {
  box-shadow: none;
}

.comment-textarea {
  label {
    font-size: 1rem;
  }

  .form-control {
    color: $gray-900; // override sumo.css
    resize: none;
  }
}

.comment-instructions {
  background-color: $gray-100;
  border-radius: 4px;
  padding: 1rem;

  .comment-instructions-item:not(:last-child) {
    margin-bottom: 0.5rem;
  }
}

.parent-comment ::v-deep .comment-body .break-word-all:last-child {
  margin-bottom: 0;
}

.error-text {
  display: none;
}

.comment-invalid {
  /* stylelint-disable selector-no-qualifying-type */
  .error-text {
    display: block;
    color: $brick;
  }
  /* stylelint-enable selector-no-qualifying-type */
}

@include media-breakpoint-up(md) {
  .comment-modal-container ::v-deep .modal-dialog-scrollable {
    justify-content: center;
    max-width: 800px; // make modal larger
  }
}
</style>
